// Transcript formatting and export builders.
//
// Pure and DOM-light on purpose: the workbook layout, the speaker-paragraph
// merging and the Excel cell-limit handling are all easy to get subtly wrong,
// and none of it needs React to test.

import { defaultSpeakerLabel } from './speakerCluster';

export function fmtBytes(n) {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

export function fmtClock(sec) {
  if (sec == null || !isFinite(sec)) return '--:--';
  const s = Math.max(0, Math.floor(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  const pad = n => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(r)}` : `${m}:${pad(r)}`;
}

export function buildTimestampedText(chunks) {
  return chunks.map(c => `[${fmtClock(c.start)}] ${c.text}`).join('\n');
}

export function downloadBlob(filename, blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function download(filename, text, mime) {
  downloadBlob(filename, new Blob([text], { type: `${mime};charset=utf-8` }));
}

/**
 * A speaker-attributed transcript, with consecutive lines from the same person
 * merged into one paragraph — far more readable than one line per segment.
 */
/** Shared by buildSpeakerText and the workbook's Segments sheet, so a
 *  speaker's display name is resolved identically everywhere: whatever the
 *  user renamed it to, else the mode-appropriate default (channel-split jobs
 *  already have "Speaker 1 (Left channel)" etc. pre-seeded into names). */
export function speakerNameOf(diarization, idx) {
  return idx == null ? 'Unlabelled' : (diarization?.names?.[idx] ?? defaultSpeakerLabel(idx));
}

export function buildSpeakerText(job, { timestamps = false } = {}) {
  const diarization = job.diarization;
  if (!diarization?.speakerCount) return null;

  const nameOf = idx => speakerNameOf(diarization, idx);

  const lines = [];
  let current;
  let buffer = [];
  let startedAt = null;

  const flush = () => {
    if (!buffer.length) return;
    const stamp = timestamps && startedAt != null ? `[${fmtClock(startedAt)}] ` : '';
    lines.push(`${stamp}${nameOf(current)}: ${buffer.join(' ')}`);
    buffer = [];
  };

  job.result.chunks.forEach((chunk, i) => {
    const speaker = diarization.speakerOf?.[i] ?? null;
    if (speaker !== current) {
      flush();
      current = speaker;
      startedAt = chunk.start;
    }
    buffer.push(chunk.text.trim());
  });
  flush();

  return lines.join('\n\n');
}

/** Every export format for one finished job, keyed by filename. */
export function exportSet(job, stem) {
  const files = {
    [`${stem}-transcript.txt`]: job.result.text,
    [`${stem}-timestamped.txt`]: buildTimestampedText(job.result.chunks),
  };
  const bySpeaker = buildSpeakerText(job, { timestamps: true });
  if (bySpeaker) files[`${stem}-speakers.txt`] = bySpeaker;
  if (job.llmSummary?.text) {
    files[`${stem}-ai-summary.txt`] =
      `AI SUMMARY — ${job.name}\n`
      + `Written locally by ${job.llmSummary.model}. NOT a verbatim record — an AI wrote\n`
      + `this and it can state things the recording never said. Check it against the\n`
      + `transcript before relying on any of it.\n\n${job.llmSummary.text}\n`;
  }
  return files;
}

export function baseName(name) {
  return (name || 'transcript').replace(/\.[^.]+$/, '');
}

// Excel refuses anything past 32,767 characters in a cell, and an hour of
// speech comfortably exceeds that. Truncate rather than let the write fail,
// and point at the full copy sitting in the same archive.
export const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export const MAX_CELL = 32000;

export function fitCell(text, filename) {
  const value = text || '';
  if (value.length <= MAX_CELL) return value;
  return `${value.slice(0, MAX_CELL)}\n\n… truncated at ${MAX_CELL.toLocaleString()} characters `
    + `(Excel's cell limit) — the complete text is in ${filename}.`;
}

/**
 * One row per recording, every text version side by side.
 *
 * Returns the raw xlsx buffer rather than a Blob so this stays DOM-free and
 * directly testable; callers wrap it for download.
 *
 * @param {Array} doneJobs
 * @returns {Promise<ArrayBuffer>}
 */
function styleHeaderRow(row) {
  row.font = { bold: true, color: { argb: 'FFFFFFFF' } };
  row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4F46E5' } };
  row.alignment = { vertical: 'middle' };
  row.height = 22;
}

/** Freeze the header + first column, and make every column filterable —
 *  the main way to navigate a sheet with many files or many rows. */
function makeNavigable(sheet, { freezeColumns = 1 } = {}) {
  sheet.views = [{ state: 'frozen', xSplit: freezeColumns, ySplit: 1 }];
  sheet.autoFilter = { from: 'A1', to: { row: 1, column: sheet.columnCount } };
}

export async function buildWorkbook(doneJobs, { whisperModel }) {
  const ExcelJS = (await import('exceljs')).default;
  const workbook = new ExcelJS.Workbook();
  workbook.created = new Date();

  // One row per FILE: an index to skim or sort/filter by duration, format,
  // speaker count — plus a flat transcript for a quick read or copy. The
  // structured, per-line detail (timestamps, who said what) lives in
  // Segments instead of being crammed into cells here, since a transcript
  // long enough to matter doesn't fit — or read usefully — in one cell.
  const files = workbook.addWorksheet('Files');
  files.columns = [
    { header: 'File', key: 'file', width: 34 },
    { header: 'Duration', key: 'duration', width: 11 },
    { header: 'Audio format', key: 'format', width: 20 },
    { header: 'Segments', key: 'segments', width: 10 },
    { header: 'Speakers', key: 'speakers', width: 10 },
    { header: 'Transcript', key: 'transcript', width: 70 },
    { header: 'Transcription model', key: 'model', width: 22 },
    { header: 'Summary model', key: 'summaryModel', width: 22 },
    { header: 'Processing time', key: 'elapsed', width: 15 },
  ];
  styleHeaderRow(files.getRow(1));

  for (const job of doneJobs) {
    const stem = baseName(job.name);
    const row = files.addRow({
      file: job.name,
      duration: job.duration ? fmtClock(job.duration) : '',
      format: job.sourceFormat || '',
      segments: job.result.chunks.length,
      speakers: job.diarization?.speakerCount || '',
      transcript: fitCell(job.result.text, `${stem}-transcript.txt`),
      model: whisperModel,
      summaryModel: job.llmSummary?.model || '',
      elapsed: job.elapsed ? fmtClock(job.elapsed) : '',
    });
    row.alignment = { wrapText: true, vertical: 'top' };
    row.height = 90;
  }
  makeNavigable(files);

  // Only when the batch actually used it — an empty sheet for a feature
  // nobody turned on is just noise. Files with no summary (toggle off for
  // them, or generation failed) are left out rather than shown blank, since
  // this sheet's only job is a fast skim of the summaries that exist.
  const summarized = doneJobs.filter(j => j.llmSummary?.text);
  if (summarized.length) {
    const summaries = workbook.addWorksheet('Summaries');
    summaries.columns = [
      { header: 'File', key: 'file', width: 34 },
      { header: 'AI summary', key: 'summary', width: 90 },
    ];
    styleHeaderRow(summaries.getRow(1));

    for (const job of summarized) {
      const row = summaries.addRow({
        file: job.name,
        summary: fitCell(job.llmSummary.text, `${baseName(job.name)}-ai-summary.txt`),
      });
      row.alignment = { wrapText: true, vertical: 'top' };
      row.height = 60;
    }
    makeNavigable(summaries);
  }

  // One row per transcript LINE across the whole batch — the actual
  // table-shaped data an hour-long call (or a 150-file batch) needs:
  // sortable, filterable by file or speaker, searchable with Excel's own
  // filter box, and every cell short enough that Excel's per-cell character
  // limit never comes into play regardless of how long a recording is.
  const segments = workbook.addWorksheet('Segments');
  segments.columns = [
    { header: 'File', key: 'file', width: 30 },
    { header: 'Start', key: 'start', width: 10 },
    { header: 'End', key: 'end', width: 10 },
    { header: 'Speaker', key: 'speaker', width: 22 },
    { header: 'Text', key: 'text', width: 80 },
  ];
  styleHeaderRow(segments.getRow(1));

  for (const job of doneJobs) {
    const diarization = job.diarization;
    job.result.chunks.forEach((chunk, i) => {
      segments.addRow({
        file: job.name,
        start: fmtClock(chunk.start),
        end: fmtClock(chunk.end),
        speaker: diarization?.speakerCount ? speakerNameOf(diarization, diarization.speakerOf?.[i] ?? null) : '',
        text: chunk.text,
      });
    });
  }
  segments.getColumn('text').alignment = { wrapText: true, vertical: 'top' };
  makeNavigable(segments, { freezeColumns: 4 });

  // A spreadsheet gets forwarded around on its own, so the caveat rides along.
  const notes = workbook.addWorksheet('About');
  notes.columns = [{ width: 110 }];
  notes.addRow(['Machine transcription — read before use']);
  notes.getRow(1).font = { bold: true, size: 13 };
  [
    '',
    'Every transcript here was produced by speech recognition, not by a human transcriber.',
    'It is an investigative aid, not a certified record. Verify against the recording before',
    'quoting any of it in a report, affidavit, or testimony.',
    '',
    'Speaker labels, where present, are a statistical guess. They are frequently wrong on',
    'crosstalk, overlapping speech, and compressed phone audio.',
    '',
    'The "AI summary" column, where present, was written by a language model. Unlike the',
    'transcript it is not a quote — it produces new sentences and can state something that',
    'was never said. Never rely on it without checking the transcript.',
    '',
    `Generated ${new Date().toLocaleString()} by LE Tech Tools, entirely in the browser.`,
  ].forEach(line => notes.addRow([line]));

  return workbook.xlsx.writeBuffer();
}

// ── capacity limits ──────────────────────────────────────────────────────────
//
// Nothing here is a hard technical cap; they are the points where a browser tab
// starts to struggle. Decoded audio is 64 KB per second (16 kHz mono float32),
// so an hour of recording is ~230 MB in memory, briefly doubled while it is
// handed to the worker. File *count* costs almost nothing by comparison,
// because each file's samples are released as soon as it finishes.

/** Past this, `file.arrayBuffer()` itself is likely to fail outright. */
export const MAX_FILE_BYTES = 2 * 1024 * 1024 * 1024;

/** Worth flagging: slow, and memory climbs. */
export const LONG_AUDIO_SECONDS = 60 * 60;

/** Genuinely risky on a typical machine — better split before running. */
export const VERY_LONG_AUDIO_SECONDS = 3 * 60 * 60;

/** Peak memory for one file's decoded samples, in bytes. */
export function decodedBytes(durationSeconds) {
  return Math.round((durationSeconds || 0) * 16000 * 4);
}

/**
 * Rough wall-clock estimate for a queue, in seconds. Deliberately conservative:
 * an estimate that runs under is far more annoying than one that runs over.
 */
export function estimateSeconds(totalAudioSeconds, { speed = 3, diarize = false, summarize = false } = {}) {
  if (!totalAudioSeconds) return 0;
  let seconds = totalAudioSeconds / Math.max(0.2, speed);
  // Diarization is a second pass over the same audio: segmentation plus one
  // embedding per speech turn.
  if (diarize) seconds += totalAudioSeconds / 2.5;
  // Summarising is dominated by generation length, not audio length, so it is
  // closer to a fixed cost per file than a multiple of duration.
  if (summarize) seconds += totalAudioSeconds / 6;
  return Math.round(seconds);
}

/** "about 25 minutes" / "about 2 hours" — vague on purpose. */
export function describeDuration(seconds) {
  if (!seconds) return '';
  if (seconds < 90) return 'under a minute';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `about ${minutes} minute${minutes === 1 ? '' : 's'}`;
  const hours = seconds / 3600;
  return hours < 1.6 ? 'about an hour' : `about ${Math.round(hours)} hours`;
}
